import { defineStore } from 'pinia';
import { StoreIdentifiersEnum } from './store.types';
import { COLORS } from '../theme';
import {
  useKnowledgeStore,
  useAdminStore,
  useNotificationStore,
  useToastStore,
  useFeedbackStore,
  useFavoritesStore,
  useSearchStore,
  useAskStore,
  useCopilotStore,
} from '@getmayday/vue-modules';
import { useAgentStore } from '@getmayday/vue-modules/agent';
import type NotificationInApp from '../composables/useNotification';
import axios from 'axios';
import { version } from '@integrations/iadvize/package.json';

export const MIN_WIDTH = 350;
const DEFAULT_POSITION = 'bottom-right' as AppPosition;
type TabContextType = {
  context: string | null;
  tabSource: string | null;
  conversationId: string | null;
};

export const useConfigStore = defineStore(StoreIdentifiersEnum.CONFIG, {
  state: () => ({
    stateLoaded: false,
    customColor: COLORS.blue.DEFAULT,
    integrationPrimaryColor: COLORS.blue.DEFAULT,
    integrationSecondaryColor: COLORS.blue.light,
    locale: 'fr-fr',
    position: DEFAULT_POSITION,
    width: MIN_WIDTH,
    isOpen: false,
    isOpenFromToast: false,
    transitionName: 'appear',
    // TEMP - Waiting for settings module/store
    resizing: false,
    // TEMP - Waiting for knowledge module/store
    modal: false,
    modalWidth: 596,
    latestVersion: '',
    notificationsInApp: [] as NotificationInApp[],
    historyRoutes: [] as EntityRoute[],
    // Lightbox
    lightbox: null as { src: string; type: string } | null,
    flowy: false,
    showVideo: false,
    hostApp: 'iadvize',
    forceMainFrame: false,
    currentTabContext: null as TabContextType | null,
    geniusTestMode: false,
  }),
  getters: {
    positionIsLeft: (state) => state.position.includes('left'),
    positionIsTop: (state) => state.position.includes('top'),
    isDeprecated: (state) => {
      const { latestVersion } = state;
      if (!latestVersion) return false;
      return version !== latestVersion;
    },
    lastEntityRoute: (state) => state.historyRoutes.at(-1) || undefined,
    previousEntityRoute: (state) => state.historyRoutes.at(-2) || undefined,
  },
  actions: {
    mutate(mutation: { [key in string]: any }): void {
      // we use path function to avoid vweReplaceState
      // path object confusion
      this.$patch((state: any) => {
        Object.keys(mutation).forEach((key) => {
          state[key] = mutation[key];
        });
      });
    },
    unsyncedMutate(mutation: { [key in string]: any }): void {
      this.$patch(mutation);
    },
    logout(): void {
      const adminStore = useAdminStore();
      const notificationStore = useNotificationStore();
      const knowledgeStore = useKnowledgeStore();
      const feedbackStore = useFeedbackStore();
      const favoritesStore = useFavoritesStore();
      const searchStore = useSearchStore();
      const toastStore = useToastStore();
      const askStore = useAskStore();
      const copilotStore = useCopilotStore();

      // Stores reset
      adminStore.reset();
      notificationStore.reset();
      knowledgeStore.reset();
      feedbackStore.reset();
      favoritesStore.reset();
      searchStore.reset();
      toastStore.reset();
      askStore.reset();
      copilotStore.reset();

      this.initRoutesHistory();
    },
    async initKnowledge(): Promise<string> {
      const knowledgeStore = useKnowledgeStore();
      const defaultKnowledge = await knowledgeStore.getKnowledges(true);

      if (!defaultKnowledge) throw new Error('MISSING_KNOWLEDGES');

      return defaultKnowledge.value;
    },
    async initAppFromLogin() {
      const adminStore = useAdminStore();
      const notificationStore = useNotificationStore();
      const favoritesStore = useFavoritesStore();

      const {
        companyColor,
        idzPrimaryColor,
        idzSecondaryColor,
        zendeskPrimaryColor,
        zendeskSecondaryColor,
        userId,
      } = storeToRefs(adminStore);

      let primaryColorPref = companyColor.value || COLORS.blue.DEFAULT;
      let secondaryColorPref = companyColor.value || COLORS.blue.DEFAULT;

      switch (this.hostApp) {
        case 'zendesk':
          primaryColorPref = zendeskPrimaryColor.value;
          secondaryColorPref = zendeskSecondaryColor.value;
          break;
        case 'iadvize':
          primaryColorPref = idzPrimaryColor.value;
          secondaryColorPref = idzSecondaryColor.value;
          break;
        default:
          break;
      }

      this.setIntegrationPrimaryColor(primaryColorPref);
      this.setIntegrationSecondaryColor(secondaryColorPref);
      notificationStore.initNotifications(userId.value as string);
      favoritesStore.fetchUserTabs('Zendesk App');
      favoritesStore.fetchUserFavorites();
      await this.initKnowledge();
      adminStore.logMeIn();
    },
    setIntegrationPrimaryColor(integrationPrimaryColor: string) {
      this.mutate({ integrationPrimaryColor });
    },
    setIntegrationSecondaryColor(integrationSecondaryColor: string) {
      this.mutate({ integrationSecondaryColor });
    },
    setLocale(locale: string) {
      this.mutate({ locale });
    },
    setStateLoaded() {
      this.unsyncedMutate({ stateLoaded: true });
    },
    setTransitionName(transitionName: string) {
      this.unsyncedMutate({ transitionName });
    },
    setPosition(position: AppPosition | undefined) {
      this.unsyncedMutate({ position: position || DEFAULT_POSITION });
    },
    setWidth(width: number) {
      if (!width || width < MIN_WIDTH) return this.setWidthSync(MIN_WIDTH);
      return this.unsyncedMutate({ width });
    },
    setWidthSync(width: number) {
      this.mutate({ width });
    },
    setResizing(resizing: boolean) {
      this.unsyncedMutate({ resizing });
    },
    setModal(modal: boolean) {
      this.mutate({ modal });
    },
    setModalWidth(modalWidth: number) {
      this.mutate({ modalWidth });
    },
    pushLastEntityRoute(route: EntityRoute) {
      this.mutate({ historyRoutes: [...this.historyRoutes, route] });
    },
    initRoutesHistory() {
      this.mutate({ historyRoutes: [] });
    },
    flushLastTwoRoutes() {
      this.mutate({ historyRoutes: this.historyRoutes.slice(0, -2) });
    },
    setImageUrl(lightbox: { src: string; type: string } | null) {
      this.unsyncedMutate({ lightbox });
    },
    setFlowy(val: boolean) {
      this.unsyncedMutate({ flowy: val });
    },
    setNotificationsInApp(notificationsInApp: NotificationInApp[]) {
      this.unsyncedMutate({ notificationsInApp });
    },
    async getLatestVersion() {
      const res = await axios.get(
        import.meta.env.VITE_APP_EXTENSION_CONFIG_URL,
        {
          headers: {
            'Content-type': 'application/json',
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
            Expires: '0',
          },
        },
      );
      const { version: latestVersion } = res.data;
      this.latestVersion = latestVersion;
      const adminStore = useAdminStore();
      adminStore.setAppVersion(version, latestVersion);
    },
    hasShowIntroVideo() {
      this.mutate({ showVideo: true });
    },
    logAppBugTracking(logEvent: AppLogPayload) {
      this.$logs(logEvent);
    },
    toggleIsOpen({ isOpenFromToast = false, method = 'CTA' }) {
      const { toggleIsOpen: toggleToastIsOpen } = useToastStore();
      toggleToastIsOpen(this.isOpen);
      this.logAppToggle(method);
      this.unsyncedMutate({ isOpen: !this.isOpen, isOpenFromToast });
      this.getLatestVersion();
    },
    initIsOpen() {
      const { toggleIsOpen: toggleToastIsOpen } = useToastStore();
      toggleToastIsOpen(true);
      this.unsyncedMutate({ isOpen: false });
    },
    setCurrentTabContext(tabContext: TabContextType) {
      this.unsyncedMutate({ currentTabContext: tabContext });
    },
    setGeniusTestMode(value: boolean) {
      const agentStore = useAgentStore();
      const copilotStore = useCopilotStore();
      this.mutate({ geniusTestMode: value });
      const agentId = agentStore.currentAgent?.id;
      copilotStore.logTestModeToggle({ value, agentId });
    },
    openGenius(trigger: GeniusTrigger, scope?: GeniusScope) {
      const agentStore = useAgentStore();
      if (scope) agentStore.persistScope(scope, trigger);
    },
    logAppToggle(method: string) {
      const adminStore = useAdminStore();

      if (!adminStore.loggedIn) return;
      this.$logs({
        topic: 'APP_TOGGLE',
        event: {
          action: this.isOpen ? 'CLOSE' : 'OPEN',
          method,
        },
        url: window.location.href,
      });
    },
  },
});
